'use client';

import { date as formatDate } from '@frameio/i18n';
import { useOpenCalendly } from '@frameio/shared/hooks';
import { formatBytes } from '@frameio/utils/formatters';
import { AlertBanner } from '@frameio/vapor';
import { useStorageLimitBanner } from '../hooks/useStorageLimitBanner';
import type { AlertBannerProps } from '../types';
import { UpgradeBannerWrapper } from './BannerWrapper';
import TEXT from './generated/TEXT/LegacyStorageLimitAlertBanner';
import { StyledButton } from './StyledButton';
import { StyledLink } from './StyledLink';
import { SupportButton } from './SupportButton';

TEXT.DEFINE(
  {
    titleWithPermission: (activeStorage: string, activeStorageLimit: string) =>
      `Storage Limit Exceeded (${activeStorage} of ${activeStorageLimit})`,

    titleNoPermission: 'Storage Limit Exceeded',

    // NO PERMISSION
    detailsNoPermissionAdobeTeam:
      'Contact your Adobe admin to purchase more licenses—each license includes storage',

    detailsNoPermissionAdobeNotTeam: 'Contact your Adobe admin for assistance.',

    detailsNoPermissionWithGrace: (ownerEmail: string, graceDate: Date) => (
      <>
        Contact account owner{' '}
        <StyledLink href={`mailto:${ownerEmail}`} target="_blank">
          ({ownerEmail})
        </StyledLink>{' '}
        for assistance before {formatDate(graceDate, 'medium')} or account will
        be locked.
      </>
    ),

    detailsNoPermission: (ownerEmail: string) => (
      <>
        Contact account owner{' '}
        <StyledLink href={`mailto:${ownerEmail}`} target="_blank">
          ({ownerEmail})
        </StyledLink>{' '}
        for assistance.
      </>
    ),

    // LEGACY FREE / STARTER / PRO → upgrade to next v8 plan
    detailsUpgradeWithGrace: (
      upgradedPlanTitle: string,
      openUpgradeModal: VoidFunction,
      graceDate: Date
    ) => (
      <>
        <StyledButton onClick={openUpgradeModal} variant="link">
          Subscribe to {upgradedPlanTitle}
        </StyledButton>{' '}
        before {formatDate(graceDate, 'medium')} or account will be locked.
      </>
    ),

    detailsUpgrade: (
      upgradedPlanTitle: string,
      openUpgradeModal: VoidFunction
    ) => (
      <StyledButton onClick={openUpgradeModal} variant="link">
        Subscribe to {upgradedPlanTitle}
      </StyledButton>
    ),

    // LEGACY TEAM → upgrade to Enterprise via Calendly
    detailsUpgradeToEnterpriseWithGrace: (
      openCalendly: VoidFunction,
      graceDate: Date
    ) => (
      <>
        <StyledButton onClick={openCalendly} variant="link">
          Chat with Sales to upgrade to Enterprise
        </StyledButton>{' '}
        before {formatDate(graceDate, 'medium')} or account will be locked.
      </>
    ),

    detailsUpgradeToEnterprise: (openCalendly: VoidFunction) => (
      <StyledButton onClick={openCalendly} variant="link">
        Chat with Sales to upgrade to Enterprise
      </StyledButton>
    ),

    // LEGACY BUSINESS → Chat with Sales via Calendly
    detailsContactSalesWithGrace: (
      openCalendly: VoidFunction,
      graceDate: Date
    ) => (
      <>
        <StyledButton onClick={openCalendly} variant="link">
          Chat with Sales
        </StyledButton>{' '}
        before {formatDate(graceDate, 'medium')} or account will be locked.
      </>
    ),

    detailsContactSales: (openCalendly: VoidFunction) => (
      <StyledButton onClick={openCalendly} variant="link">
        Chat with Sales
      </StyledButton>
    ),

    // LEGACY ENTERPRISE → contact support only
    detailsEnterpriseWithGrace: (graceDate: Date) => (
      <>
        <SupportButton>Contact support</SupportButton> for assistance before{' '}
        {formatDate(graceDate, 'medium')} or account will be locked.
      </>
    ),

    detailsEnterprise: (
      <>
        <SupportButton>Contact support</SupportButton> for assistance.
      </>
    ),
  },
  { StyledButton, StyledLink, SupportButton }
);

const LEGACY_BUSINESS_TIER = 'BUSINESS';

type GetDetailsOptions = {
  canManageBilling: boolean;
  graceDate: Date | null;
  isEnterprise: boolean;
  isManagedByAdobe: boolean;
  isTeam: boolean;
  isBusiness: boolean;
  openCalendly: VoidFunction;
  openUpgradeModal: VoidFunction;
  ownerEmail: string;
  upgradedPlanTitle: string | undefined;
};

const getDetails = ({
  canManageBilling,
  graceDate,
  isEnterprise,
  isManagedByAdobe,
  isTeam,
  isBusiness,
  openCalendly,
  openUpgradeModal,
  ownerEmail,
  upgradedPlanTitle,
}: GetDetailsOptions) => {
  if (!canManageBilling) {
    if (isManagedByAdobe) {
      return isTeam
        ? TEXT.detailsNoPermissionAdobeTeam
        : TEXT.detailsNoPermissionAdobeNotTeam;
    }
    const noPermissionGraceDate = isEnterprise ? null : graceDate;
    return noPermissionGraceDate
      ? TEXT.detailsNoPermissionWithGrace(ownerEmail, noPermissionGraceDate)
      : TEXT.detailsNoPermission(ownerEmail);
  }

  if (isBusiness) {
    return graceDate
      ? TEXT.detailsContactSalesWithGrace(openCalendly, graceDate)
      : TEXT.detailsContactSales(openCalendly);
  }
  if (isTeam) {
    return graceDate
      ? TEXT.detailsUpgradeToEnterpriseWithGrace(openCalendly, graceDate)
      : TEXT.detailsUpgradeToEnterprise(openCalendly);
  }
  if (isEnterprise) {
    return graceDate
      ? TEXT.detailsEnterpriseWithGrace(graceDate)
      : TEXT.detailsEnterprise;
  }

  // FREE / STARTER / PRO
  if (upgradedPlanTitle) {
    return graceDate
      ? TEXT.detailsUpgradeWithGrace(
          upgradedPlanTitle,
          openUpgradeModal,
          graceDate
        )
      : TEXT.detailsUpgrade(upgradedPlanTitle, openUpgradeModal);
  }

  return null;
};

export function LegacyStorageLimitAlertBanner({
  account,
  fetchPlansData,
  upgradedPlan,
}: AlertBannerProps) {
  const {
    canManageBilling,
    showBanner,
    tier,
    isEnterprise,
    isTeam,
    activeStorage,
    activeStorageLimit,
    ownerEmail,
    isManagedByAdobe,
    graceDate,
    upgradedPlanTitle,
    openUpgradeModal,
  } = useStorageLimitBanner(account, fetchPlansData, upgradedPlan);

  const isBusiness = (tier as string) === LEGACY_BUSINESS_TIER;
  const openCalendly = useOpenCalendly();

  if (!showBanner) return null;

  const title = canManageBilling
    ? TEXT.titleWithPermission(
        formatBytes(activeStorage ?? 0),
        formatBytes(activeStorageLimit ?? 0)
      )
    : TEXT.titleNoPermission;

  const details = getDetails({
    canManageBilling: Boolean(canManageBilling),
    graceDate,
    isEnterprise,
    isManagedByAdobe,
    isTeam,
    isBusiness,
    openCalendly,
    openUpgradeModal,
    ownerEmail,
    upgradedPlanTitle,
  });

  if (!details) return null;

  return (
    <UpgradeBannerWrapper onHoverIntent={fetchPlansData}>
      <AlertBanner details={details} title={title} variant="notice" />
    </UpgradeBannerWrapper>
  );
}
